import { z } from 'zod';
import { isoInstantSchema, moneySchema } from "./event-schemas.js";
/**
 * Refunds, ticket revocation and organizer settlement visibility.
 *
 * Three contracts that arrive together because they are three views of one
 * event: money goes back, the ticket it bought stops working, and the
 * organizer's position changes. Keeping them in one file makes the vocabulary
 * consistent — there is exactly one word for a refund's state, and every
 * surface reads it from here.
 *
 * What is deliberately absent, and will stay absent until CABS rules on it:
 *
 *   * any commission or platform fee percentage;
 *   * any Nigerian tax treatment;
 *   * any reserve or rolling-hold percentage;
 *   * any payout schedule, payout instruction or payout execution.
 *
 * Settlement here means *visibility*: what was collected, what was given back,
 * and what that leaves. It is not an instruction to pay anybody, and the
 * contract says so in its own field names rather than leaving a client to infer
 * it from documentation nobody reads.
 */
// --- Refund state -----------------------------------------------------------
/**
 * Where a refund is.
 *
 * Deliberately parallel to the payment vocabulary, including the parts that are
 * uncomfortable. `REVIEW_REQUIRED` exists for the same reason it exists on a
 * payment: when a provider's answer and GateCheckPro's records disagree, the
 * honest state is "a human must look", not a guess in either direction.
 */
export const REFUND_STATUSES = [
    /** Somebody asked. Nothing has been decided and no money has moved. */
    'REQUESTED',
    /** Declined. The tickets stay valid and the customer keeps what they bought. */
    'REJECTED',
    /** Authorised by someone holding `FINANCE_APPROVE`. The tickets are void. */
    'APPROVED',
    /** The provider has been asked to pay it out. */
    'PROVIDER_PENDING',
    /** The provider confirmed the money went back. */
    'SUCCEEDED',
    /** The payout did not happen. It may be retried; the tickets stay void. */
    'FAILED',
    /** The provider's answer cannot be reconciled. A human must decide. */
    'REVIEW_REQUIRED',
];
export const refundStatusSchema = z.enum(REFUND_STATUSES);
/**
 * Why a refund was given.
 *
 * A closed list, because this is the field finance reports group by and free
 * text cannot be grouped. The story goes in `reasonDetail`.
 */
export const REFUND_REASON_CATEGORIES = [
    'CUSTOMER_REQUEST',
    'ORGANIZER_GOODWILL',
    'DUPLICATE_PURCHASE',
    'EVENT_CANCELLED',
    'EVENT_RESCHEDULED',
    'FRAUD',
    'OPERATIONAL_ERROR',
];
export const refundReasonCategorySchema = z.enum(REFUND_REASON_CATEGORIES);
// --- Requesting a refund ----------------------------------------------------
/**
 * What an organizer may say when asking for a refund.
 *
 * Note what a caller cannot send: a currency, a provider, a payout destination
 * or a ticket belonging to another order. The currency is the order's, the
 * destination is wherever the money came from, and the tickets are checked
 * against the order server-side. A caller that could name any of those could
 * send money somewhere it never came from.
 */
export const refundRequestSchema = z.object({
    /**
     * The tickets this refund covers, and what is attributed to each.
     *
     * Naming tickets rather than a quantity is what makes a partial refund exact:
     * "these two of the four", not "two of them, pick some".
     */
    tickets: z
        .array(z.object({
        ticketId: z.string().uuid(),
        /**
         * May be zero. An organizer refunding a pair as one gesture can put the
         * whole sum on one ticket and void both, and the record should say what
         * they actually decided rather than a distribution nobody chose.
         */
        amountMinor: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    }))
        .min(1)
        .max(200),
    /** Must equal the sum of the lines. Stated so a client cannot mis-total silently. */
    amountMinor: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    reasonCategory: refundReasonCategorySchema,
    reasonDetail: z.string().trim().max(1000).nullable().default(null),
    /**
     * Supplied by the caller so two clicks on "refund" produce one refund.
     *
     * Required rather than optional: an idempotency key a client may omit is one
     * that will be omitted exactly when the network is bad enough to need it.
     */
    idempotencyKey: z.string().trim().min(8).max(200),
});
/**
 * Approving or rejecting. A decision, and the reason for it.
 *
 * Requires `FINANCE_APPROVE`, which is separately MFA-gated — so the approval
 * step already carries a second factor without any route having to remember it.
 */
export const refundDecisionSchema = z.object({
    decision: z.enum(['APPROVE', 'REJECT']),
    note: z.string().trim().max(1000).nullable().default(null),
});
// --- Reading a refund -------------------------------------------------------
export const refundTicketSchema = z.object({
    ticketId: z.string().uuid(),
    ticketNumber: z.string(),
    amountMinor: z.number().int().min(0),
    /** Whether this ticket is now void. Always true once the refund is approved. */
    voided: z.boolean(),
});
export const refundSchema = z.object({
    id: z.string().uuid(),
    refundNumber: z.string(),
    orderId: z.string().uuid(),
    orderNumber: z.string(),
    status: refundStatusSchema,
    amount: moneySchema,
    reasonCategory: refundReasonCategorySchema,
    reasonDetail: z.string().nullable(),
    tickets: z.array(refundTicketSchema),
    requestedAt: isoInstantSchema,
    decidedAt: isoInstantSchema.nullable(),
    settledAt: isoInstantSchema.nullable(),
});
/** What happened, in order. Append-only, so this is the whole story. */
export const refundHistoryEntrySchema = z.object({
    occurredAt: isoInstantSchema,
    fromStatus: refundStatusSchema.nullable(),
    toStatus: refundStatusSchema,
    reasonCode: z.string(),
});
export const refundDetailSchema = refundSchema.extend({
    history: z.array(refundHistoryEntrySchema),
});
// --- What a customer is told ------------------------------------------------
/**
 * The customer-facing account of a voided ticket.
 *
 * A deliberately smaller vocabulary than the organizer's. An attendee should
 * not read `FRAUD` or `OPERATIONAL_ERROR` off their own ticket: the first is an
 * accusation and the second is an internal admission, and neither is the
 * platform's to publish on the organizer's behalf. Both collapse to
 * `ORGANIZER_ACTION`, which is true.
 */
export const CUSTOMER_TICKET_VOID_REASONS = [
    'REFUNDED',
    'EVENT_CANCELLED',
    'ORGANIZER_ACTION',
];
export const customerTicketVoidReasonSchema = z.enum(CUSTOMER_TICKET_VOID_REASONS);
export const ticketVoidNoticeSchema = z.object({
    reason: customerTicketVoidReasonSchema,
    voidedAt: isoInstantSchema,
    /** Present only when money actually went back, and only then. */
    refundedAmount: moneySchema.nullable(),
});
// --- Settlement visibility --------------------------------------------------
/**
 * An organizer's financial position, derived from the accounting ledger.
 *
 * Every field here is a sum of postings that exist. There is no field for a
 * commission, a tax, a reserve or a payout date, and adding one would require a
 * ruling that does not exist — so a surface rendering this can honestly say
 * "collected, refunded, and the difference", and cannot accidentally imply a
 * payment is scheduled.
 */
export const settlementPositionSchema = z.object({
    organizationId: z.string().uuid(),
    currency: z.string().length(3),
    /** Confirmed collections. Gross, because nothing has been deducted. */
    grossCollectedMinor: z.number().int().min(0),
    /** Refunds that have actually settled. */
    refundedMinor: z.number().int().min(0),
    /** Refunds approved and not yet settled. Money already promised back. */
    refundsInFlightMinor: z.number().int().min(0),
    /**
     * What the ledger says CABS owes this organizer before any commercial term is
     * applied. Explicitly not a payout amount, and named so nobody can read it as
     * one.
     */
    netBeforeCommercialTermsMinor: z.number().int(),
    /**
     * Provider charges GateCheckPro observed on this organizer's collections.
     *
     * Reported, never subtracted. Who bears a provider fee is a commercial
     * question CABS has not answered, and answering it by arithmetic here would
     * be inventing the policy rather than surfacing the evidence.
     */
    observedProviderChargesMinor: z.number().int().min(0),
    asOf: isoInstantSchema,
});
/**
 * One movement, as finance reads it.
 *
 * A statement line is a journal entry, not a database row somebody chose to
 * show. Anything visible here can be traced to postings that balance.
 */
export const settlementStatementLineSchema = z.object({
    journalEntryId: z.string().uuid(),
    occurredAt: isoInstantSchema,
    sourceKind: z.enum(['ORDER_SALE', 'REFUND', 'CORRECTION']),
    sourceId: z.string().uuid(),
    reference: z.string(),
    /** Positive increases what is owed to the organizer; negative reduces it. */
    changeMinor: z.number().int(),
    currency: z.string().length(3),
});
export const settlementStatementSchema = z.object({
    position: settlementPositionSchema,
    lines: z.array(settlementStatementLineSchema),
});
// --- Refusals ---------------------------------------------------------------
/**
 * Why a refund was refused, in a vocabulary a surface can act on.
 *
 * Classified from a typed reason code or a constraint name — never from message
 * prose, which is written for humans and changes when a human improves it.
 */
export const REFUND_REFUSAL_REASONS = [
    /** The order was never paid, so there is nothing to give back. */
    'ORDER_NOT_PAID',
    /**
     * The order owed nothing. A free ticket has no payment to reverse, and the
     * schema cannot express one: migration 0057 forbids a payment intent against
     * a zero-total order, and a refund must name one.
     */
    'ORDER_WAS_FREE',
    /** More than was collected, once every live refund on the order is counted. */
    'REFUND_EXCEEDS_COLLECTION',
    /** The line amounts do not add up to the refund. */
    'REFUND_LINES_DO_NOT_BALANCE',
    /** One of the named tickets belongs to a different order. */
    'TICKET_NOT_ON_ORDER',
    /** One of the named tickets is already void. */
    'TICKET_ALREADY_VOID',
    /** One of the named tickets is already covered by a live refund. */
    'TICKET_ALREADY_REFUNDED',
    /** The refund is not in a state where this decision means anything. */
    'REFUND_NOT_PENDING_DECISION',
    /** Already decided, and the second decision disagrees with the first. */
    'REFUND_ALREADY_DECIDED',
    /** The same idempotency key was used for a materially different request. */
    'IDEMPOTENCY_KEY_REUSED',
    /** No provider is configured, so a payout cannot even be attempted. */
    'NO_PAYMENT_PROVIDER_CONFIGURED',
    /** The configured provider cannot refund this collection. */
    'PROVIDER_CANNOT_REFUND',
];
export const refundRefusalReasonSchema = z.enum(REFUND_REFUSAL_REASONS);
/**
 * Narrows a refusal this client understands, or `null`.
 *
 * Null rather than throwing, so a surface a release behind a new reason shows
 * the generic refusal instead of matching nothing and rendering an empty state.
 */
export function asRefundRefusalReason(value) {
    const parsed = refundRefusalReasonSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
// --- Platform reconciliation ------------------------------------------------
/**
 * What CABS operations can ask the finance data about itself.
 *
 * Read-only, and deliberately so. Every write this report might tempt somebody
 * into — editing a posting, forcing a refund's state, correcting a balance —
 * either does not exist or is refused by the schema, and inventing an operator
 * command here would be building the authority before anybody had decided who
 * should hold it.
 *
 * The checks come in two kinds and the difference matters more than any single
 * number. A `MUST_BE_ZERO` check restates an invariant the database already
 * enforces; a non-zero answer does not mean somebody has work to do, it means
 * the enforcement is not working and that is an incident. A `WORK_QUEUE` check
 * counts things legitimately waiting for a human, where non-zero is ordinary.
 * Reporting both in one list without the distinction would train an operator to
 * treat the first like the second.
 */
export const RECONCILIATION_CHECKS = [
    /** An entry whose debits and credits differ. Enforced by trigger since 0059. */
    'UNBALANCED_JOURNAL_ENTRIES',
    /** An entry with fewer than two postings. Money from nowhere. */
    'SINGLE_SIDED_JOURNAL_ENTRIES',
    /** A ticket marked void with no record of who voided it. Enforced since 0058. */
    'VOID_TICKETS_WITHOUT_PROVENANCE',
    /** A void ticket whose credential an offline gate would still admit. */
    'VOID_TICKETS_WITH_A_LIVE_CREDENTIAL',
    /** A settled refund the ledger never recorded. */
    'SETTLED_REFUNDS_WITHOUT_A_LEDGER_ENTRY',
    /** A refund whose payout the provider could not account for. */
    'PAYOUTS_OF_UNKNOWN_OUTCOME',
    /** A refund waiting for a person to decide something. */
    'REFUNDS_AWAITING_A_HUMAN',
    /**
     * A collected order with no sale entry.
     *
     * A work queue rather than an invariant, because orders paid before the
     * accounting ledger existed have none and never will until a backfill is
     * ruled on. The number should be stable; a rising one is a defect.
     */
    'COLLECTED_ORDERS_WITHOUT_A_SALE_ENTRY',
];
export const reconciliationFindingSchema = z.object({
    check: z.enum(RECONCILIATION_CHECKS),
    expectation: z.enum(['MUST_BE_ZERO', 'WORK_QUEUE']),
    count: z.number().int().min(0),
    /**
     * A bounded sample of what was found, for somebody about to investigate.
     *
     * Ids only. A reconciliation report is read by operators across every tenant,
     * and there is no reason for it to carry an organizer's commercial detail.
     */
    sample: z.array(z.string()).max(20),
});
export const financeReconciliationSchema = z.object({
    generatedAt: isoInstantSchema,
    findings: z.array(reconciliationFindingSchema),
    /** True when every `MUST_BE_ZERO` check answered zero. */
    invariantsHold: z.boolean(),
});
// --- Finding the order to refund --------------------------------------------
/**
 * Discovery, and why it is its own contract.
 *
 * Raising a refund needs authoritative ticket ids, and until this existed the
 * only way to get one was to already know it. An operator with a customer on
 * the phone has an order number or an email address, and a workflow that
 * requires them to have the primary key first is not a workflow.
 *
 * Everything here is read-only and `FINANCE_VIEW`, which is already
 * second-factor gated at the capability. It is deliberately not a general
 * order-management surface: the fields are the ones a refund decision needs and
 * stop there.
 */
/** Enough to recognise the order a caller is looking for. */
export const financeOrderSummarySchema = z.object({
    orderId: z.string().uuid(),
    orderNumber: z.string(),
    status: z.string(),
    placedAt: isoInstantSchema,
    amount: moneySchema,
    /**
     * What the order was placed under, from the immutable snapshot rather than
     * the account — a customer may since have changed their address, and the
     * order still belongs to whoever placed it.
     */
    purchaserEmail: z.string(),
    eventTitle: z.string(),
    /** How much of this order has already been given back or promised back. */
    alreadyClaimedMinor: z.number().int().min(0),
    /**
     * What a refund could still be raised for, computed by the same rule the
     * refund command enforces.
     *
     * Stated by the server so a surface never has to reimplement the
     * over-refund policy and then disagree with it — offering a customer a
     * refund the next call refuses is worse than not offering one.
     */
    refundableMinor: z.number().int().min(0),
});
export const financeOrderTicketSchema = z.object({
    ticketId: z.string().uuid(),
    ticketNumber: z.string(),
    status: z.enum(['ISSUED', 'VOID']),
    unitPriceMinor: z.number().int().min(0),
    /**
     * Whether a refund that has not been rejected or failed already names it.
     *
     * The precise predicate the refund command checks, so a surface can grey out
     * exactly the tickets that would be refused and no others.
     */
    alreadyClaimed: z.boolean(),
});
/** One line per refund already raised against the order. */
export const financeOrderRefundSchema = z.object({
    refundId: z.string().uuid(),
    refundNumber: z.string(),
    status: refundStatusSchema,
    amount: moneySchema,
    reasonCategory: refundReasonCategorySchema,
    requestedAt: isoInstantSchema,
});
export const financeOrderDetailSchema = financeOrderSummarySchema.extend({
    tickets: z.array(financeOrderTicketSchema),
    refunds: z.array(financeOrderRefundSchema),
});
/**
 * A page of results.
 *
 * `nextCursor` is opaque and null when the page is the last one. Opaque because
 * a cursor a client can construct is a cursor a client will construct, and it
 * would then depend on an ordering this surface is free to change.
 */
export const financeOrderPageSchema = z.object({
    orders: z.array(financeOrderSummarySchema),
    nextCursor: z.string().nullable(),
});
export const refundSummarySchema = z.object({
    refundId: z.string().uuid(),
    refundNumber: z.string(),
    orderId: z.string().uuid(),
    orderNumber: z.string(),
    status: refundStatusSchema,
    amount: moneySchema,
    reasonCategory: refundReasonCategorySchema,
    requestedAt: isoInstantSchema,
    decidedAt: isoInstantSchema.nullable(),
});
export const refundPageSchema = z.object({
    refunds: z.array(refundSummarySchema),
    nextCursor: z.string().nullable(),
});
/**
 * How a caller narrows the search.
 *
 * Exact matches only. A substring search across a tenant's order history would
 * turn a finance read into an address-book export, and neither thing an
 * operator actually has — an order number read off a confirmation, or the email
 * the customer is calling about — is a fragment.
 */
/** `true`/`false` as a real boolean, however it arrived. */
const queryBooleanSchema = z.union([
    z.boolean(),
    z.enum(['true', 'false']).transform((value) => value === 'true'),
]);
export const financeOrderQuerySchema = z.object({
    eventId: z.string().uuid().optional(),
    orderNumber: z.string().trim().max(32).optional(),
    purchaserEmail: z.string().trim().toLowerCase().max(320).optional(),
    /**
     * Only orders money was actually collected for. Defaults to true.
     *
     * Coerced from a string because these schemas parse a URL query as readily as
     * a JSON body, and `?collectedOnly=false` arriving as the string "false" —
     * which is truthy — would silently mean the opposite of what was asked.
     */
    collectedOnly: queryBooleanSchema.default(true),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    cursor: z.string().max(200).optional(),
});
export const refundQuerySchema = z.object({
    status: refundStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    cursor: z.string().max(200).optional(),
});
//# sourceMappingURL=refund-schemas.js.map